/**
 * Why the session ended, carried across the hard navigation to /login (IN-116).
 *
 * The interceptor ends a session with `window.location.href = '/login'`, which throws away
 * every piece of in-memory state — so the sign-in page has no way to know whether it is
 * showing because a token quietly expired or because someone changed this person's access
 * mid-task. Without that, a non-technical user is dropped at an unexplained login screen
 * and reads it as the app breaking.
 *
 * `sessionStorage`, deliberately:
 *  - it survives the reload, which is the whole requirement, and `clearAuth()` does not
 *    touch it (it clears `localStorage` + the query cache);
 *  - it is per TAB, so the explanation appears on the tab the person was working in and
 *    not on some other window they open tomorrow;
 *  - it is not a URL parameter, so the reason never lands in a link, a bookmark or a
 *    referrer header.
 *
 * Read with {@link takeSessionEndNotice}, which CLEARS as it reads — the sentence is shown
 * once, not on every subsequent visit to the sign-in page.
 */
const KEY = 'ps_session_end_notice'

export type SessionEndNotice = {
  /**
   * `ACCESS_CHANGED` is the server's `ErrorResponse.code` for the refusal. `EXPIRED` is the
   * client's own: a studio page was opened with no live session (a bookmark, an email link
   * after "sign out everywhere" — F42 / D62).
   */
  reason: 'ACCESS_CHANGED' | 'EXPIRED'
  /** The studio whose access changed, from the client's OWN store — never from the response. */
  studio?: string
}

export function rememberSessionEndNotice(notice: SessionEndNotice): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(notice))
  } catch {
    // Private windows and blocked site data both throw. An unexplained sign-in page is
    // worse copy, not a broken app — never let this fail the sign-out it accompanies.
  }
}

/** Read the pending notice and clear it, so it is shown exactly once. */
export function takeSessionEndNotice(): SessionEndNotice | null {
  try {
    const raw = sessionStorage.getItem(KEY)
    if (!raw) return null
    sessionStorage.removeItem(KEY)
    const parsed = JSON.parse(raw) as SessionEndNotice
    return parsed?.reason === 'ACCESS_CHANGED' || parsed?.reason === 'EXPIRED' ? parsed : null
  } catch {
    return null
  }
}
